import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import * as SharedActions from '../store-shared/shared.actions';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';
import {IDebtorDetails} from '../../quotes/models/debtors';
import {IStorage} from '../../../shared/shared-models/storage/storage';
import {IMessage, IMessageAutoOrderUpdate} from '../models';
import {IDepartment} from '../../../shared/shared-models/stock/departments';
import {ISharedStoreSettings} from '../../../shared/shared-models/firebase/shared-store-settings';
import {IColleagues} from '../../user-access/models';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {TLineColour} from '../../settings-new/models';

export interface SharedState {
  colleagues: IColleagues;
  currentVersion: string;
  debtors: Record<string, Record<string, IDebtorDetails>>;
  departments: Record<string, IDepartment[]>;
  errors: IGlobalError[];
  isColleaguesLoading: boolean;
  isDebtorsLoading: boolean;
  isStorageLoading: boolean;
  isSuppliersLoading: boolean;
  lineColours: Record<string, TLineColour>;
  loadingDepartments: boolean,
  messagesFromApp: Record<string, Record<string, IMessage<IMessageAutoOrderUpdate[]>[]>>;
  storageUrl: IStorage;
  storeSettings: Record<string, ISharedStoreSettings>;
  suppliers: Record<string, Record<string, ISupplier>>;
}

export const initialSharedState: SharedState = {
  colleagues: {},
  currentVersion: '',
  debtors: {},
  departments: {},
  errors: [],
  isColleaguesLoading: false,
  isDebtorsLoading: false,
  isStorageLoading: false,
  isSuppliersLoading: false,
  lineColours: {},
  loadingDepartments: false,
  messagesFromApp: {},
  storageUrl: undefined,
  storeSettings: {},
  suppliers: {},
};

const createSharedReducer: ActionReducer<SharedState> = createReducer(
  initialSharedState,
  // ====================================================================================================
  // Suppliers
  // ====================================================================================================
  //#region Suppliers
  on(SharedActions.getStoreSuppliersByUserSelectedStore, (state: SharedState) => ({
    ...state,
    isSuppliersLoading: true,
  })),
  on(SharedActions.getStoreSuppliersByStore, (state: SharedState) => ({
    ...state,
    isSuppliersLoading: true,
  })),
  on(SharedActions.getStoreSuppliersByStoreSuccess, (state: SharedState, {suppliers, store}) => ({
    ...state,
    isSuppliersLoading: false,
    suppliers: {
      ...state.suppliers,
      [store.storeId]: suppliers,
    },
  })),
  on(SharedActions.getStoreSuppliersByStoreFailure, (state: SharedState, {error}) => ({
    ...state,
    isSuppliersLoading: false,
    error: [...state.errors, error],
  })),
  //#endregion

  // ====================================================================================================
  // Debtors
  // ====================================================================================================
  //#region Debtors
  on(SharedActions.getStoreDebtorsByUserSelectedStore, (state: SharedState) => ({
    ...state,
    isDebtorsLoading: true,
  })),
  on(SharedActions.getStoreDebtorsByStore, (state: SharedState) => ({
    ...state,
    isDebtorsLoading: true,
  })),
  on(SharedActions.getStoreDebtorsByStoreSuccess, (state: SharedState, {debtors, store}) => ({
    ...state,
    isDebtorsLoading: false,
    debtors: {
      ...state.debtors,
      [store.storeId]: debtors,
    },
  })),
  on(SharedActions.getStoreDebtorsByStoreFailure, (state: SharedState, {error}) => ({
    ...state,
    isDebtorsLoading: false,
    error: [...state.errors, error],
  })),
  //#endregion

  // ====================================================================================================
  // Line Colours
  // ====================================================================================================
  //#region Line Colours
  on(SharedActions.getLineColour, (state) => ({
    ...state,
  })),
  on(SharedActions.getLineColourSuccess, (state, {store, lineColour}) => ({
    ...state,
    lineColours: {
      ...state.lineColours,
      [store.storeId]: lineColour,
    },
  })),
  on(SharedActions.getLineColourFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  //#endregion

  // ====================================================================================================
  // Departments
  // ====================================================================================================
  //#region Departments
  on(SharedActions.fetchStoreDepartments, (state) => ({
    ...state,
    loadingDepartments: true,
  })),
  on(SharedActions.fetchStoreDepartmentsSuccess, (state, {storeId, departments, storeSettings}) => ({
    ...state,
    loadingDepartments: false,
    departments: {...state.departments, [storeId]: departments},
    storeSettings: {...state.storeSettings, [storeId]: storeSettings},
  })),
  on(SharedActions.fetchStoreDepartmentsFailure, (state, {error}) => ({
    ...state,
    loadingDepartments: false,
    errors: [...state.errors, error],
  })),
  //#endregion
  // ====================================================================================================
  // Get Colleagues
  // ====================================================================================================
  //#region Get Colleagues
  on(SharedActions.getColleagues, (state) => ({
    ...state,
    isColleaguesLoading: true,
  })),
  on(SharedActions.getColleaguesSuccess, (state, {users}) => ({
    ...state,
    colleagues: users,
    isColleaguesLoading: false,
  })),
  on(SharedActions.getColleaguesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isColleaguesLoading: false,
  })),
  //#endregion
  // ====================================================================================================
  // Firestore Storage
  // ====================================================================================================
  //#region Firestore Storage
  on(SharedActions.getDownloadUrlFromStorage, (state, {storage}) => ({
    ...state,
    isStorageLoading: true,
    storageUrl: {} as IStorage,
  })),
  on(SharedActions.getDownloadUrlFromStorageSuccess, (state, {storage}) => ({
    ...state,
    isStorageLoading: false,
    storageUrl: storage,
  })),
  on(SharedActions.getDownloadUrlFromStorageFailure, (state, {error}) => ({
    ...state,
    isStorageLoading: false,
    storageUrl: {} as IStorage,
    errors: [...state.errors, error],
  })),
  on(SharedActions.clearDownloadUrlFromStorage, (state) => ({
    ...state,
    isStorageLoading: false,
    storageUrl: undefined,
  })),
  //#endregion
  // ====================================================================================================
  // Messages From App
  // ====================================================================================================
  //#region Messages From App
  on(SharedActions.getMessagesFromAppForAutoOrderUpdates, (state) => ({
    ...state,
  })),
  on(SharedActions.getMessagesFromAppForAutoOrderUpdatesSuccess, (state, {store, messages, messageType}) => ({
    ...state,
    messagesFromApp: {
      ...state.messagesFromApp,
      [store.storeId]: {
        ...state.messagesFromApp[store.storeId],
        [messageType]: messages,
      },
    },
  })),
  on(SharedActions.getMessagesFromAppForAutoOrderUpdatesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(SharedActions.setInitialVersion, (state, {version}) => ({
    ...state,
    currentVersion: version,
  })),
);
//#endregion
export const sharedReducer = (state: SharedState, action: Action): SharedState => createSharedReducer(state, action);
